import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  Notification,
  CHAT_MESSAGE_UNREAD,
  ORDER_DOCUMENT_UPLOADED,
  isChatNotification,
  isDocumentUploadedNotification,
  documentUploadedTitle,
} from 'types/notifications'
import { AlertVariant } from 'types/alert'
import { selectToken } from 'store/users/users.selectors'
import { notificationsUpdateSuccess } from 'store/notifications/notifications.actions'
import { addAlert } from 'store/alerts/alerts.actions'
import { fetchFeNotificationsFromOtk } from 'requests/notifications'
import { showChatNotifications, showDocumentUploadedNotifications } from 'utils/browser-notifications'
import { isNotificationLeaderTab } from 'utils/notification-tab-leader'
import { isNotificationsModuleEnabled } from 'utils/is-notifications-module-enabled'

const lastChatNotificationAtByOrderId: Record<string, number> = {}
/** Toast once per notification id (two uploads → two toasts). */
const toastedDocumentNotificationIds = new Set<string>()

const emitBrowserChatNotifications = (normalized: Notification[]) => {
  const chatItems = normalized.filter(isChatNotification)
  const newItems = chatItems.filter((it) => {
    if (!it.orderId) return false
    const ts = Date.parse(it.createdAt)
    if (!Number.isFinite(ts)) return false

    const prevTs = lastChatNotificationAtByOrderId[it.orderId] ?? 0
    if (ts <= prevTs) return false
    return true
  })

  if (newItems.length) {
    newItems.forEach((it) => {
      const ts = Date.parse(it.createdAt)
      if (Number.isFinite(ts)) lastChatNotificationAtByOrderId[it.orderId] = ts
    })
    showChatNotifications({ items: newItems })
  }
}

const emitDocumentUploadedToasts = (normalized: Notification[], dispatch: (action: unknown) => void) => {
  const docs = normalized.filter(isDocumentUploadedNotification)
  if (!docs.length) return

  const fresh = docs.filter((it) => {
    const key = it.id || `${it.orderId}:${it.createdAt}:${it.fileName || ''}`
    if (toastedDocumentNotificationIds.has(key)) return false
    toastedDocumentNotificationIds.add(key)
    return true
  })

  if (!fresh.length) return

  if (isNotificationLeaderTab()) {
    showDocumentUploadedNotifications({ items: fresh })
  }

  fresh.forEach((it) => {
    dispatch(
      addAlert({
        lifetime: 1000 * 25,
        header: documentUploadedTitle(it.orderId),
        text: it.fileName || '',
        variant: AlertVariant.INFO,
        withLineBreaks: true,
      }),
    )
  })
}

type InboxSseRow = {
  id?: string
  type?: string
  orderId?: string
  isInternal?: boolean
  createdAt?: string
  fileName?: string
  payload?: {
    orderId?: string
    isInternal?: boolean
    fileName?: string
  }
}

const normalizeInboxSseRow = (item: InboxSseRow): Notification | null => {
  const orderId = item.orderId || item.payload?.orderId
  const { createdAt } = item
  if (!orderId || !createdAt) return null

  const type = (item.type as Notification['type']) || CHAT_MESSAGE_UNREAD

  if (type === ORDER_DOCUMENT_UPLOADED) {
    return {
      id: item.id,
      type: ORDER_DOCUMENT_UPLOADED,
      orderId,
      isInternal: false,
      createdAt,
      fileName: item.fileName || item.payload?.fileName,
    }
  }

  if (type && type !== CHAT_MESSAGE_UNREAD) {
    return null
  }

  return {
    id: item.id,
    type: CHAT_MESSAGE_UNREAD,
    orderId,
    isInternal: Boolean(item.isInternal ?? item.payload?.isInternal),
    createdAt,
  }
}

/**
 * P3: chat + document inbox push via OTK SSE `/notifications/v1/stream` (not chat-be Socket.IO).
 * Mount only when REACT_APP_NOTIFICATIONS_MODULE=true.
 * Toast only from SSE (not from list hydrate).
 */
export const InboxNotificationsMonitor: React.FC = () => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)

  const moduleEnabled = isNotificationsModuleEnabled()
  const active = Boolean(moduleEnabled && token)

  useEffect(() => {
    if (!active || !token) return

    const serverBase = process.env.REACT_APP_V3_SERVER
    if (!serverBase) return

    const url = `${serverBase}/notifications/v1/stream?token=${encodeURIComponent(token)}`

    const clearReconnectTimer = () => {
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    const closeEventSource = () => {
      if (eventSourceRef.current) eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    const onNotification = (event: MessageEvent) => {
      let data: InboxSseRow[] = []
      try {
        const parsed = JSON.parse(event.data as string)
        data = Array.isArray(parsed) ? parsed : [parsed]
      } catch {
        return
      }

      const normalized: Notification[] = data.map(normalizeInboxSseRow).filter((item): item is Notification => item !== null)

      if (!normalized.length) return

      emitBrowserChatNotifications(normalized)
      emitDocumentUploadedToasts(normalized, dispatch)

      fetchFeNotificationsFromOtk(token)
        .then((list) => dispatch(notificationsUpdateSuccess(list)))
        .catch((err) => {
          console.error('notifications sync after inbox SSE failed', err)
          dispatch(notificationsUpdateSuccess(normalized))
        })
    }

    let connect: () => void = () => {}

    const scheduleReconnect = () => {
      clearReconnectTimer()
      closeEventSource()

      const attempt = reconnectAttemptRef.current
      const delayMs = Math.min(30000, 1000 * 2 ** attempt)
      reconnectAttemptRef.current = Math.min(attempt + 1, 10)

      reconnectTimeoutRef.current = window.setTimeout(() => {
        connect()
      }, delayMs)
    }

    connect = () => {
      clearReconnectTimer()
      closeEventSource()

      const es = new EventSource(url) as any
      eventSourceRef.current = es
      es.addEventListener('notification', onNotification)

      es.onopen = () => {
        reconnectAttemptRef.current = 0
      }

      es.onerror = () => {
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      clearReconnectTimer()
      closeEventSource()
    }
  }, [active, dispatch, token])

  return null
}
